import React, { useEffect, useLayoutEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Outlet, useLocation } from "react-router-dom";
import { trackUserAction } from "../features/dashai/utils/trackUserAction";
import {
  setFilterContext,
  initializeFilterDimensions,
  selectFilterMeta,
} from "../slices/filterDimensionsSlice";
import { isFilterContextConsistent } from "../utils/Homepage/homepageUtils";
import AnalyticsSidebar from "./AnalyticsSidebar";
import AnalyticsTopBar from "./AnalyticsTopBar";
import ImpersonationBanner, { IMPERSONATION_BANNER_HEIGHT } from "./ImpersonationBanner";
import { useScrollDepth } from "../analytics/useScrollDepth";
import { useTopBarNavigation } from "../hooks/useWorkspaceUiConfig";
import { NavigationShellContext } from "../contexts/NavigationShellContext";
import { DashboardQuestionProvider } from "../contexts/DashboardQuestionContext";
import { QuestionsPanelSlot } from "../features/dashai/components/dashboard-panel/QuestionsPanel";
import { TOP_BAR_SIDE_INSET } from "../features/dashai/components/dashai/TopBar/navStyles";

const DASHBOARD_NAMES = {
  "/market_overview": "Market Overview",
  "/market_deepdive": "Market Deepdive",
  "/execution_view": "Execution View",
  "/physical_location": "Physical Location",
  "/physical_franchise": "Physical Franchise",
  "/accounts": "Accounts",
};

const DWELL_THRESHOLD_MS = 10_000;

// Page spacing under the top bar. The side inset is the bar's own side
// padding, so a page's edges line up with the wordmark and the account menu.
const TOP_BAR_PAGE_INSETS = {
  "--page-inset-x": TOP_BAR_SIDE_INSET,
  "--page-inset-top": "32px",
  "--page-offset-x": "0px",
  "--page-width": "100%",
};

// The shell around the dashboard pages. As the layout route in App.jsx it
// renders the matched page through <Outlet>, and it stays mounted while the
// user moves between pages. A page outside that route can wrap itself instead
// by passing children.
const GeneralLayout = ({ children }) => {
  const dispatch = useDispatch();
  const selectedWorkspace = useSelector((state) => state.auth.selectedWorkspace);
  const selectedCountry = useSelector((state) => state.filterDimensions?.meta?.country);
  const isImpersonating = useSelector((state) => state.auth.isImpersonating);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const id_client = useSelector((state) => state.auth.id_client);
  const currentCountry = useSelector((state) => state.filters.currentCountry);
  const countries = useSelector((state) => state.auth.countries);
  const filterContextStatus = useSelector((state) => state.auth.status);
  const filterMeta = useSelector(selectFilterMeta);
  const location = useLocation();
  const trackedRef = useRef(null);
  // Feed scroll depth into page_engaged from the inner scroll container (the
  // body itself never scrolls). Applies to the dashboard routes GeneralLayout
  // wraps. DashAI and the other shells are not covered.
  const scrollRef = useRef(null);
  useScrollDepth(scrollRef);
  // Which navigation shell this workspace gets: the top bar when its UI config
  // turns it on, the sidebar otherwise.
  const withTopBar = useTopBarNavigation();

  // The scroll container outlives each page, so a new page would open at the
  // previous page's scroll offset. Reset it before paint.
  useLayoutEffect(() => {
    scrollRef.current?.scrollTo?.({ top: 0 });
  }, [location.pathname]);

  // Initialize the DashONE dashboard filter dimensions. This lives here — in the
  // layout that wraps every filter-consuming dashboard route — rather than in the
  // app root, so the DashAI chat routes (`/`, `/ai/*`), which render outside
  // GeneralLayout and never read this slice, don't trigger the dashboard
  // filter-dimension fetches on mount.
  //
  // Fetch once per (id_client, country): GeneralLayout unmounts on chat routes and
  // remounts on return, so the redux `meta.initialized` + context match guards
  // against re-fetching the full dimension set every time the user comes back from
  // /ai. A context change flips `initialized` false (setFilterContext), re-arming it.
  //
  // Skip the dispatch when (id_client, currentCountry) is inconsistent
  // — e.g. id_client=pepsi_fr (only has FR data) with currentCountry=AT carried over
  // from a previous workspace/session. The per-dimension routes return 5xx on such
  // pairs and page Slack. filtersSlice selects a valid country when the countries
  // fetch settles, so a brief skip here is benign — the effect re-fires then.
  useEffect(() => {
    if (!isAuthenticated || !id_client || !currentCountry) return;
    if (!isFilterContextConsistent(currentCountry, countries)) return;
    // Hold dashboard fetches until a workspace switch resolves. During
    // "switching" the country has been cleared but the new workspace's country
    // is not yet chosen, so firing now would carry a stale/empty context.
    if (filterContextStatus !== "ready") return;

    const alreadyInitialized =
      filterMeta.initialized &&
      filterMeta.id_client === id_client &&
      filterMeta.country === currentCountry;
    if (alreadyInitialized) return;

    dispatch(setFilterContext({ id_client, country: currentCountry }));
    dispatch(initializeFilterDimensions({ id_client, country: currentCountry }));
  }, [
    dispatch,
    isAuthenticated,
    id_client,
    currentCountry,
    countries,
    filterContextStatus,
    filterMeta.initialized,
    filterMeta.id_client,
    filterMeta.country,
  ]);

  // Track dashboard usage after 10s dwell time (User Analytics → Client × Market / Usage by Feature)
  useEffect(() => {
    const dashboardName = DASHBOARD_NAMES[location.pathname];
    if (!dashboardName) return;
    if (trackedRef.current === location.pathname) return;

    const timer = setTimeout(() => {
      trackedRef.current = location.pathname;
      // Tracked via the shared trackUserAction util → dashaiAdminTrackLogin
      // Firebase function; logs an error and no-ops if VITE_FIREBASE_FUNCTIONS_URL
      // is unset. The util takes uid from the Firebase ID token, never the body.
      trackUserAction({
        eventType: "dashboard",
        idClient: selectedWorkspace?.key || null,
        metadata: { page: dashboardName, path: location.pathname, country: selectedCountry || null },
      });
    }, DWELL_THRESHOLD_MS);

    return () => clearTimeout(timer);
  }, [location.pathname, selectedWorkspace, selectedCountry]);

  // One tree for both shells: only the navigation slot and the flex direction
  // change. The scroll container must stay the same element, because
  // useScrollDepth binds its listener once and would keep listening to a
  // removed node if a workspace switch swapped the whole layout.
  //
  // The impersonation banner is fixed to the top of the window. With the top
  // bar the whole column starts below it; with the sidebar only the content
  // does, as the sidebar runs the full height.
  //
  // With the top bar, the pages share the bar's side inset. Each page reads
  // these properties and falls back to its own sidebar-shell spacing, so the
  // sidebar layout does not move. `--page-offset-x` and `--page-width` clear
  // the fixed left offset some pages keep for a sidebar.
  //
  // The scroll container always reserves the scrollbar's width, so a view
  // that grows past the window does not push the page sideways.
  //
  // The page area is a positioned row around the scroll container, so the
  // Questions panel can sit beside the page or over it without leaving the
  // area under the navigation.
  return (
    <div
      style={{
        display: "flex",
        flexDirection: withTopBar ? "column" : "row",
        height: "100vh",
        fontFamily: "Inter, sans-serif",
        backgroundColor: "#f7f8fa",
        overflow: "hidden",
        paddingTop: withTopBar && isImpersonating ? IMPERSONATION_BANNER_HEIGHT : 0,
      }}
    >
      <ImpersonationBanner fullWidth={withTopBar} />
      {withTopBar ? <AnalyticsTopBar /> : <AnalyticsSidebar />}
      <DashboardQuestionProvider>
        <div
          style={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: "flex",
            position: "relative",
            marginTop: !withTopBar && isImpersonating ? IMPERSONATION_BANNER_HEIGHT : 0,
          }}
        >
          <div
            ref={scrollRef}
            style={{
              flex: 1,
              minWidth: 0,
              overflow: "auto",
              scrollbarGutter: "stable",
              ...(withTopBar ? TOP_BAR_PAGE_INSETS : null),
            }}
          >
            <NavigationShellContext.Provider value={withTopBar ? "topbar" : "sidebar"}>
              {children ?? <Outlet />}
            </NavigationShellContext.Provider>
          </div>
          <QuestionsPanelSlot />
        </div>
      </DashboardQuestionProvider>
    </div>
  );
};

export default GeneralLayout;
